import { useEffect, useState } from 'react'
import { Download, Radio } from 'lucide-react'
import { Badge, Button, Collapsible, CopyField, Drawer, Notice } from '@/components/play'
import { downloadObsSetup } from '@/lib/obsPackage'
import { useStreamLiveStatus } from '@/hooks/useStreamLiveStatus'
import { useProductEvent } from '@/hooks/useProductEvent'
import { cn } from '@/lib/utils'
import type { StreamResource } from '@/types/events'

interface OBSSetupPanelProps {
  eventId: string
  eventName: string
  streams: StreamResource[]
  onEnableLiveShow: () => Promise<StreamResource>
  liveShowBusy?: boolean
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function OBSSetupPanel({
  eventId,
  eventName,
  streams,
  onEnableLiveShow,
  liveShowBusy = false,
  open,
  onOpenChange,
}: OBSSetupPanelProps) {
  const track = useProductEvent(eventId)
  const [preparing, setPreparing] = useState(false)
  const [enabling, setEnabling] = useState(false)
  const [enableError, setEnableError] = useState('')
  const [error, setError] = useState('')
  const [downloadedSignature, setDownloadedSignature] = useState<string | null>(
    null,
  )
  const programme = streams.find(
    (stream) => stream.attributes.source_role === 'programme',
  )
  const cameras = streams.filter(
    (stream) => stream.attributes.source_role !== 'programme',
  )
  const returnUrl =
    programme?.attributes.connection_details?.external_ingress_url || undefined
  const setupSignature = JSON.stringify({
    eventName,
    programme: programme ? [programme.id, returnUrl ?? null] : null,
    cameras: cameras
      .map((camera) => [
        camera.id,
        camera.attributes.name,
        camera.attributes.connection_details?.external_egress_url,
      ])
      .sort(),
  })
  const downloaded = downloadedSignature !== null
  const stale = downloaded && downloadedSignature !== setupSignature
  const { hasTrack } = useStreamLiveStatus(
    programme?.attributes.participant_identity,
    programme?.attributes.status,
  )
  const showState = hasTrack
    ? 'Live show receiving'
    : programme?.attributes.route_status === 'grace'
      ? 'Live show reconnecting'
      : programme?.attributes.status === 'error'
        ? 'Live show connection needs attention'
        : 'Waiting for OBS'

  useEffect(() => {
    setDownloadedSignature(null)
    setError('')
    setEnableError('')
  }, [eventId])

  async function enableLiveShow() {
    if (enabling || liveShowBusy || programme) return
    setEnabling(true)
    setEnableError('')
    try {
      await onEnableLiveShow()
    } catch {
      setEnableError('The live show couldn’t be added. Try again.')
    } finally {
      setEnabling(false)
    }
  }

  function download() {
    if (preparing) return
    setPreparing(true)
    setError('')
    try {
      downloadObsSetup({
        eventId,
        eventName,
        returnUrl,
        streams: cameras.map((camera) => ({
          id: camera.id,
          name: camera.attributes.name,
          sourceRole: camera.attributes.source_role,
          egressUrl:
            camera.attributes.connection_details?.external_egress_url || '',
        })),
      })
      setDownloadedSignature(setupSignature)
      track('obs_setup_downloaded', {
        ...(returnUrl && programme ? { programme_id: programme.id } : {}),
        camera_count: cameras.length,
      })
    } catch {
      setError('The OBS setup couldn’t be prepared. Try downloading again.')
    } finally {
      setPreparing(false)
    }
  }

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      eyebrow={eventName}
      title="Your cameras, in OBS"
      description="Take your cameras into OBS and make the show your own."
      className="ph-no-capture ph-no-record"
    >
      {programme ? (
        <div
          className="my-xl flex items-center gap-s rounded-2xl bg-neutral-3 p-m"
          role="status"
        >
          <Radio
            className={cn(
              'size-5 shrink-0',
              hasTrack ? 'text-success' : 'text-neutral-7',
            )}
          />
          <div>
            <p className="font-medium">{showState}</p>
            <p className="text-sm text-neutral-7">
              {cameras.length} {cameras.length === 1 ? 'camera' : 'cameras'}{' '}
              in this event
            </p>
          </div>
        </div>
      ) : (
        <p className="my-8 text-sm text-neutral-7">
          {cameras.length} {cameras.length === 1 ? 'camera' : 'cameras'} in this event
        </p>
      )}

      <ol className="space-y-6">
        <li className="flex gap-4">
          <span className="text-neutral-7">01</span>
          <div>
            <p className="font-semibold">Download and unzip</p>
            <p className="mt-1 text-sm text-neutral-7">
              {returnUrl
                ? 'Your cameras and the live show destination are included.'
                : 'A scene for each camera and a silent camera grid.'}
            </p>
          </div>
        </li>
        <li className="flex gap-4">
          <span className="text-neutral-7">02</span>
          <div>
            <p className="font-semibold">
              {returnUrl ? 'Import both parts in OBS' : 'Import your camera scenes'}
            </p>
            <p className="mt-1 text-sm text-neutral-7">
              Scene Collection → Import: choose scenes.json.
              {returnUrl ? (
                <>
                  <br />
                  Profile → Import: choose the streambridge folder.
                  <br />
                  Select both imported items from their menus.
                </>
              ) : (
                <> Select the imported collection from the menu.</>
              )}
            </p>
          </div>
        </li>
        <li className="flex gap-4">
          <span className="text-neutral-7">03</span>
          <div>
            <p className="font-semibold">
              {returnUrl ? 'Start your show' : 'Check your cameras'}
            </p>
            <p className="mt-1 text-sm text-neutral-7">
              {returnUrl
                ? 'Check the camera picture and audio, then start streaming in OBS. Your live show will appear here.'
                : 'Choose a camera scene and check its picture and audio. Use your OBS settings to record or stream.'}
            </p>
          </div>
        </li>
      </ol>

      <p className="mt-6 text-sm text-neutral-7">
        Camera scenes use their own audio. The camera grid is silent. Add
        commentary in OBS when you need it.
      </p>
      {cameras.length === 0 && (
        <p className="mt-4 text-sm">
          You can prepare OBS now. Add cameras here and download again to
          include them.
        </p>
      )}
      {error && (
        <Notice tone="error" alert className="mt-4">{error}</Notice>
      )}
      <Button loading={preparing} onClick={() => download()} className="mt-8 w-full">
        <Download />
        {preparing
          ? 'Preparing OBS setup…'
          : downloaded
            ? 'Download OBS setup again'
            : 'Download OBS setup'}
      </Button>
      {downloaded && (
        <p role="status" className="mt-3 text-sm text-neutral-7">
          {stale
            ? 'Your setup has changed. Download again to get the latest version.'
            : 'Downloaded. Open START-HERE.txt for the import steps.'}
        </p>
      )}
      <p className="mt-3 text-xs text-neutral-7">
        {returnUrl
          ? 'Keep this download private. It contains your camera and show addresses.'
          : 'Keep this download private. It contains your camera addresses.'}
      </p>

      {!programme && (
        <section
          className="mt-xl pt-l shadow-[inset_0_1px_0_var(--neutral-4)]"
          aria-labelledby="obs-live-show-title"
        >
          <h2 id="obs-live-show-title" className="flex items-center gap-xs font-semibold">
            Live show
            <Badge tone="quiet">Optional</Badge>
          </h2>
          <p className="mt-2 text-sm text-neutral-7">
            Send your finished OBS mix back to this event.
          </p>
          <Button
            variant="secondary"
            loading={enabling || liveShowBusy}
            onClick={() => void enableLiveShow()}
            className="mt-4"
          >
            {enabling || liveShowBusy ? 'Adding live show…' : 'Add live show'}
          </Button>
          {enableError && (
            <Notice tone="error" alert className="mt-3">{enableError}</Notice>
          )}
        </section>
      )}
      {programme && !returnUrl && (
        <p role="status" className="mt-6 text-sm text-neutral-7">
          The live show address is unavailable. This download includes your
          cameras. Refresh the event to try the live show setup again.
        </p>
      )}

      {returnUrl && (
        <Collapsible className="mt-8" trigger="Connect OBS manually">
          <p className="text-sm text-neutral-7">
            In Settings → Stream, choose Custom. Paste this into Server and
            leave Stream Key empty.
          </p>
          <CopyField label="Server" copyLabel="Copy OBS server address" value={returnUrl} className="mt-3" />
        </Collapsible>
      )}
    </Drawer>
  )
}
